<script lang="ts">
  import { onMount } from 'svelte';
  import { headerDisplayStore, loadingStore } from '../stores/General';
  import { reportDetailStore } from '../stores/Reports';
  import ReportDetailOverlay from './overlays/ReportDetailOverlay.svelte';
  import { navigate } from 'svelte-navigator';
  import Button from '../components/button/RouteButton.svelte';
  import { getList, remove } from '../api';
  import CustomIcon from '../components/CustomIcon.svelte';
  import NewTable from '../components/table/Table.svelte';
  import { Column } from '../components/table/types.svelte';

  const module_name = 'reports';
  const width = "150";
  const columns: Column[] = [
    {
      label: 'Title',
      dataKey: 'title',
      sortBy: 'text',
      renderBy: 'formatter',
      isClickable: true,
      width
    },
    {
      renderBy: 'actions',
      actions: [
        { key: 'view', label: 'View', icon: 'view' },
        { key: 'edit', label: 'Edit', icon: 'edit' },
        { key: 'dlt', label: 'Delete', icon: 'dlt' },
      ],
      width: "110",
    },
  ];
  let filters = [{ label: 'Title', key: 'title', filter: 'text' }];
  let pageSize = 10;
  let data = [];
  let count = 0;
  let page = 1;
  let sortBy = 'title';
  let sortOrder = 'ASC';
  let filter = {};
  let showDeleteDialog = false;
  let selectedData = null;
  let reportDetail = false;
  let loading = false;
  const tableId = "reports";
  let pagination = { page, pageSize, count };

  onMount(() => {
    headerDisplayStore.SetTitle('Reports');
    loadData();
  });

  function editAction(value) {
    navigate(`/report/edit/${value.report_id}`);
  }

  function deleteAction(value) {
    selectedData = value;
    showDeleteDialog = true;
  }

  function deleteData() {
    loading = true;
    remove(module_name, [selectedData.report_id])
      .then(() => {
        showDeleteDialog = false;
        headerDisplayStore.ShowNotification({
          title: 'Success',
          subtitle: `Report ${selectedData.title} deleted successfully`,
        });
        loadData();
      })
      .catch((error) => {
        loading = false;
        showDeleteDialog = false;
        headerDisplayStore.ShowError({
          title: 'Server error',
          subtitle: error?.message,
        });
      });
  }

  function openDetail(reportType) {
    reportDetailStore.set(reportType);
    reportDetail = true;
  }

  function prepareReportType({ params, ...reportType }) {
    let p = typeof params === 'string' ? JSON.parse(params) : [];
    p = p.map((param) => ({
      paramName: param.param_name,
      displayName: param.display_name,
      type: param.type,
      defaultValue: param.default_value,
    }));
    const disabled = reportType.disabled ? 'Yes' : 'No';
    return { ...reportType, disabled, params: p };
  }

  function loadData() {
    let mainSettingsArray = JSON.parse(
      localStorage.getItem(`_tableMainSettings_${tableId}`)
    );

    pagination = {...pagination, ...mainSettingsArray?.pagination};

    if (pagination !== null){
      pageSize = pagination.pageSize;
      page = pagination.page;
    }

    loadingStore.on(module_name);
    getList(module_name, pageSize, page, sortBy, sortOrder, filter)
      .then((response) => {
        loadingStore.off(module_name);
        data = response.data.map(prepareReportType);
        pagination.count = response.count;
      })
      .catch((error) => {
        loadingStore.off(module_name);
        headerDisplayStore.ShowError({
          title: 'Server error',
          subtitle: error?.message,
        });
      });
  }

  function onPageChange(event) {
    let mainSettingsArray= JSON.parse(
      localStorage.getItem(`_tableMainSettings_${tableId}`)
    );

      mainSettingsArray ={...mainSettingsArray, pagination:event.detail};

      localStorage.setItem(
      `_tableMainSettings_${tableId}`,
      JSON.stringify(mainSettingsArray)
    );

    page = event.detail.page;
    pageSize = event.detail.pageSize;
    loadData();
  }

  function onSortChange(event) {
    sortBy = event.detail.sortBy;
    sortOrder = event.detail.sortOrder;
    loadData();
  }

  function onFilterChange(event) {
    filter = event.detail.filter;
    loadData();
  }

  function onActionBtnClick({ detail }) {
    const { action, row } = detail;

    switch (action.key) {
      case 'view':
        openDetail(row);
        break;
      case 'edit':
        editAction(row);
        break;
      case 'dlt':
        deleteAction(row);
        break;
    }
  }

  function onColumnClick({ detail }) {
    const { column: _, row } = detail;
    openDetail(row);
  }
</script>

<div class="flex flex-col h-full">
  <div class="w-full sm:px-6">
    <div class="py-2 md:py-2">
      <div class="sm:flex items-center justify-end">
        <div>
          <Button name="New Report" route="/report/edit" />
        </div>
      </div>
    </div>
    <NewTable
      bind:data
      {columns}
      dataKey="report_id"
      {filters}
      defaultConfig={{
        tableId,
        stickyHeader: true,
        stickyLastColumn: true,
        multilineColumns: true,
      }}
      {pagination}
      sorting={{ sortBy, sortOrder }}
      on:pageChange={onPageChange}
      on:sortChange={onSortChange}
      on:onFilterChange={onFilterChange}
      on:actionClick={onActionBtnClick}
      on:columnClick={onColumnClick}
    />
  </div>
  {#if reportDetail}
    <ReportDetailOverlay
      onClose={() => {
        reportDetail = false;
      }}
    />
  {/if}
  {#if showDeleteDialog}
    <div
      class="fixed z-100 inset-0 overflow-y-auto"
      aria-labelledby="modal-title"
      role="dialog"
      aria-modal="true"
    >
      <div
        class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0"
      >
        <div
          class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          aria-hidden="true"
        />

        <span class="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true"
          >&#8203;</span
        >

        <div
          class="inline-block align-bottom bg-white rounded-lg px-6 pt-5 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle w-full sm:max-w-690 sm:w-full sm:p-6"
        >
          <div class="block absolute top-5 right-5 z-100 ">
            <div
              type="button"
              class="hover:text-gray-500 border-transparent cursor-pointer"
              on:click={() => (showDeleteDialog = false)}
            >
              <span class="sr-only">Close</span>

              <CustomIcon width="18" height="18" name="close_dialog" />
            </div>
          </div>
          <div>
            <h3 class="text-center mb-3 font-bold">Delete report?</h3>
            <ul>
              <li>• {selectedData.title}</li>
            </ul>
          </div>
          <div class="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
            <button
              on:click={deleteData}
              type="button"
              class="inline-flex px-4 py-2 justify-center ml-6 rounded-md border border-gray-300 bg-yellow-500 text-sm font-medium text-yellow-800 hover:bg-yellow-400 focus:z-10 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600"
            >
              <span>Delete</span>
            </button>
            <button
              type="button"
              on:click={() => (showDeleteDialog = false)}
              class="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:w-auto sm:text-sm"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  {/if}
</div>
